import React, { useMemo } from 'react';

import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { AnyAri } from '@atlassian/ari/any-ari';

import { ConnectorSources } from '../../../../../../common/constants/schemas/connector-sources';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { FederatedIcon } from '../../../../../../common/ui/federated-icon';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { SearchResult } from '../../../../../../common/ui/search-result';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { isEligibleFor3PClickIngestion } from '../../../../../../common/utils/is-eligible-for-3p-click-ingestion';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import { ResultAnalytics } from '../../../../../../controllers/analytics/result-analytics';
import type { ResultDerivedDataProps } from '../../../result-derived-data';
import type { searchFederatedResult_AtlassianSearchPage$key } from './__generated__/searchFederatedResult_AtlassianSearchPage.graphql';
import { SearchFederatedEmail } from './search-federated-email';

const styles = cssMap({
	typeLabelContainer: {
		'&::first-letter': {
			textTransform: 'capitalize',
		},
	},
});

const getLabel = (entityNoun: string) => entityNoun.replace(/-/g, ' ');

enum ResourceType {
	EMAIL = 'email',
}

export const SearchFederatedResult = ({
	preparedResultAttributes,
	intentDetectionTriggered,
	node,
	position,
	cloudId,
	primaryProduct,
	primarySubProduct,
	appMode,
	selectedProduct,
	searchQuery,
	searchSessionId,
	mainContentWidth,
	selectedSites,
	productConfigs,
	thirdPartyConfigs,
}: SearchResultProps<searchFederatedResult_AtlassianSearchPage$key> &
	ResultDerivedDataProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchFederatedResult_AtlassianSearchPage on SearchResultFederated {
				id
				lastModifiedDate
				url
				title
				description
				...searchFederatedEmail_AtlassianSearchPage
			}
		`,
		node,
	);

	const { resourceId, resourceOwner: product, resourceType } = AnyAri.parse(data.id);

	const metadata = useMemo(() => {
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: resourceType,
			entityType: resourceType,
			contentId: resourceId,
			product,
			// it's federated so always false in this component
			is3PSmartLink: false,
			connectorType: ConnectorSources.Federated,
			...getSiteMetadata(data.id, cloudId, selectedSites),
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [resourceId, product, resourceType, data.id, cloudId, selectedSites]);

	const is3PClickIngestionEnabled = isEligibleFor3PClickIngestion(thirdPartyConfigs || {});

	const icon = (
		<FederatedIcon productConfigs={productConfigs} product={product} resourceType={resourceType} />
	);

	if (resourceType === ResourceType.EMAIL) {
		return (
			<ResultAnalytics
				preparedResultAttributes={preparedResultAttributes}
				intentDetectionTriggered={intentDetectionTriggered}
				metadata={metadata}
				position={position}
				is3PClickIngestionEnabled={is3PClickIngestionEnabled}
			>
				{({
					onResultClicked,
					onResultContextMenu: onResultCtxMenuClicked,
					onCopyLinkButtonClicked,
					onSummarizeInChatButtonClicked,
					onContextMenuCopyButtonClicked,
					onContextMenuButtonClicked,
				}) => (
					<SearchFederatedEmail
						node={data}
						position={position}
						product={product}
						cloudId={cloudId}
						primaryProduct={primaryProduct}
						primarySubProduct={primarySubProduct}
						selectedProduct={selectedProduct}
						searchQuery={searchQuery}
						searchSessionId={searchSessionId}
						appMode={appMode}
						icon={icon}
						onResultClicked={onResultClicked}
						onResultContextMenu={onResultCtxMenuClicked}
						onCopyLinkButtonClicked={onCopyLinkButtonClicked}
						onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
						onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
						onContextMenuButtonClicked={onContextMenuButtonClicked}
						mainContentWidth={mainContentWidth}
						selectedSites={selectedSites}
						productConfigs={productConfigs}
					/>
				)}
			</ResultAnalytics>
		);
	}

	const { id, lastModifiedDate, url, title, description } = data;

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const label = getLabel(metadata.entityType);

	const headerMetadataParts = [
		['label', <Box xcss={styles.typeLabelContainer}>{label}</Box>],
		['last-modified', lastModifiedComponent],
	];

	return (
		<ResultAnalytics
			preparedResultAttributes={preparedResultAttributes}
			intentDetectionTriggered={intentDetectionTriggered}
			metadata={metadata}
			position={position}
			is3PClickIngestionEnabled={is3PClickIngestionEnabled}
		>
			{({
				onResultClicked,
				onResultContextMenu: onResultCtxMenuClicked,
				onCopyLinkButtonClicked,
				onSummarizeInChatButtonClicked,
				onContextMenuCopyButtonClicked,
				onContextMenuButtonClicked,
			}) => (
				<SearchResult
					productConfigs={productConfigs}
					id={id}
					product={product}
					productSelected={!!selectedProduct}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultCtxMenuClicked}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
					description={<DescriptionBody description={processExcerpt(description)} />}
					icon={icon}
					headerMetadataParts={headerMetadataParts}
					title={enrichExcerpt(title)}
					url={url}
					isThirdParty
					cloudId={cloudId}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					position={position}
					partial={data}
					appMode={appMode}
					mainContentWidth={mainContentWidth}
				/>
			)}
		</ResultAnalytics>
	);
};

export const isSearchFederatedResult = (
	node?: any,
): node is searchFederatedResult_AtlassianSearchPage$key => {
	return node?.__typename === 'SearchResultFederated';
};
